/**
 * Input validation utilities using Zod
 * Provides type-safe validation with comprehensive error messages
 */

import { z } from "zod";

const MAX_LIST_NAME = 100;
const MAX_INGREDIENT = 200;
const MAX_QUANTITY = 50;
const MAX_EMAIL = 255;
const MIN_USERNAME = 3;
const MAX_USERNAME = 30;

// ============================================================================
// Validation Schemas
// ============================================================================

/**
 * List validation schemas
 */
export const listSchemas = {
  name: z
    .string()
    .min(1, "validation.listNameEmpty")
    .max(MAX_LIST_NAME, "validation.listNameTooLong")
    .refine(
      (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
      "validation.listNameInvalidChars"
    ),

  color: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, "validation.invalidColorFormat")
    .optional()
    .nullable(),

  create: z.object({
    name: z
      .string()
      .min(1, "validation.listNameEmpty")
      .max(MAX_LIST_NAME, "validation.listNameTooLong")
      .refine(
        (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
        "validation.listNameInvalidChars"
      ),
    color: z
      .string()
      .regex(/^#[0-9A-F]{6}$/i, "validation.invalidColorFormat")
      .optional()
      .nullable(),
  }),

  update: z.object({
    name: z
      .string()
      .min(1, "validation.listNameEmpty")
      .max(MAX_LIST_NAME, "validation.listNameTooLong")
      .refine(
        (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
        "validation.listNameInvalidChars"
      )
      .optional(),
    color: z
      .string()
      .regex(/^#[0-9A-F]{6}$/i, "validation.invalidColorFormat")
      .optional()
      .nullable(),
  }),
};

/**
 * Ingredient validation schemas
 */
export const ingredientSchemas = {
  text: z
    .string()
    .min(1, "validation.ingredientEmpty")
    .max(MAX_INGREDIENT, "validation.ingredientTooLong")
    .refine(
      (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
      "validation.ingredientInvalidChars"
    ),

  category: z.enum(["meat", "seafood", "produce", "fruit", "dairy", "bakery", "pantry", "other"], {
    message: "validation.invalidCategory",
  }),

  quantity: z.string().max(MAX_QUANTITY, "validation.quantityTooLong").optional().nullable(),

  create: z.object({
    text: z
      .string()
      .min(1, "validation.ingredientEmpty")
      .max(MAX_INGREDIENT, "validation.ingredientTooLong")
      .refine(
        (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
        "validation.ingredientInvalidChars"
      ),
    category: z
      .enum(["meat", "seafood", "produce", "fruit", "dairy", "bakery", "pantry", "other"])
      .optional(),
    quantity: z.string().max(MAX_QUANTITY, "validation.quantityTooLong").optional().nullable(),
    listId: z.string().min(1, "validation.invalidListId"),
  }),

  update: z.object({
    text: z
      .string()
      .min(1, "validation.ingredientEmpty")
      .max(200, "validation.ingredientTooLong")
      .refine(
        (val) => !/<script|<iframe|javascript:|on\w+=/i.test(val),
        "validation.ingredientInvalidChars"
      )
      .optional(),
    category: z
      .enum(["meat", "seafood", "produce", "fruit", "dairy", "bakery", "pantry", "other"])
      .optional(),
    quantity: z.string().max(50, "validation.quantityTooLong").optional().nullable(),
    completed: z.boolean().optional(),
  }),
};

/**
 * Sharing validation schemas
 */
export const sharingSchemas = {
  email: z.string().email("validation.invalidEmail").max(255, "validation.emailTooLong"),

  invite: z.object({
    email: z.string().email("validation.invalidEmail").max(MAX_EMAIL, "validation.emailTooLong"),
  }),
};

/**
 * Profile validation schemas
 */
export const profileSchemas = {
  username: z
    .string()
    .min(MIN_USERNAME, "validation.usernameTooShort")
    .max(MAX_USERNAME, "validation.usernameTooLong")
    .regex(/^[a-z0-9_]+$/, "validation.usernameInvalidFormat"),

  update: z.object({
    username: z
      .string()
      .min(MIN_USERNAME, "validation.usernameTooShort")
      .max(MAX_USERNAME, "validation.usernameTooLong")
      .regex(/^[a-z0-9_]+$/, "validation.usernameInvalidFormat")
      .optional(),
    image: z.string().url().optional().nullable(),
  }),
};

/**
 * Preferences validation schemas
 */
export const preferencesSchemas = {
  update: z.object({
    theme: z.enum(["light", "dark", "system"]).optional(),
    language: z.enum(["en", "zh-CN", "zh-TW"]).optional(),
    activeListId: z.string().min(1, "validation.invalidListId").nullable().optional(),
    weeklyMealDraft: z.boolean().optional(),
  }),
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type-safe validation result
 */
export type ValidationResult<T> = { success: true; data: T } | { success: false; error: string };

/**
 * Validates data against a Zod schema and returns type-safe result
 */
export function validate<T>(schema: z.ZodSchema<T>, data: unknown): ValidationResult<T> {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  // Format Zod errors into user-friendly message
  const firstError = result.error.issues[0];

  // If schema messages were provided as translation keys (e.g. "validation.xxx"),
  // return the key so callers can localize using the i18n system.
  // Otherwise fall back to a generic validation key.
  const rawMessage = firstError?.message || "validation.invalidInput";

  return { success: false, error: rawMessage };
}

/**
 * Translate a validation error key (or raw message) using a translation function.
 * This helper centralizes common interpolation values for schema constraints.
 */
export function translateValidationError(
  t: (key: string, opts?: Record<string, any>) => string,
  err?: string
) {
  if (!err) return "";

  // If it's not a validation key, return it as-is (likely a raw error string)
  if (!err.startsWith("validation.")) return err;

  const key = err.replace(/^validation\./, "");

  // Common interpolation values for length/limits used across schemas
  const paramsMap: Record<string, Record<string, any>> = {
    listNameTooLong: { max: MAX_LIST_NAME },
    ingredientTooLong: { max: MAX_INGREDIENT },
    quantityTooLong: { max: MAX_QUANTITY },
    emailTooLong: { max: MAX_EMAIL },
    listNameTooLongShort: { max: MAX_LIST_NAME },
    usernameTooShort: { min: MIN_USERNAME },
    usernameTooLong: { max: MAX_USERNAME },
  };

  const params = paramsMap[key] ?? {};

  return t(key, params);
}
